export const acceptedFileTypes = [
  "text/plain",
  "text/csv",
  "application/pdf",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
];

export const acceptedFileTypesPrint = [
  "Text",
  "Comma-separated values (CSV)",
  "Adobe Portable Document Format (PDF)",
  "Microsoft Word",
  "OpenDocument text document",
  "Microsoft Excel",
  "JPEG images",
  "PNG images",
];

export const acceptedFileTypesString = acceptedFileTypes.join(" ");

/**
 *
 * @param fileSizeInBytes
 * @param includeBytes
 * @returns {string}
 * @see Modified from {@link https://developer.mozilla.org/en-US/docs/Web/API/File_API/Using_files_from_web_applications}
 */

export function convertFileSizeToFriendlyFormat(
  fileSizeInBytes: number,
  includeBytes: any
) {
  let sOutput = fileSizeInBytes + " bytes";
  const aMultiples = ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  for (
    let nMultiple = 0, nApprox = fileSizeInBytes / 1024;
    nApprox > 1;
    nApprox /= 1024, nMultiple++
  ) {
    sOutput = nApprox.toFixed(3) + " " + aMultiples[nMultiple];

    if (includeBytes) sOutput += " (" + fileSizeInBytes + " bytes)";
  }

  return sOutput;
}

/**
 * @function
 * @param {number} fileSizeInBytes usually from `file.size`
 * @param {boolean} includeBytes set to `true` if you want the `fileSizeInBytes` value returned in the string as well.
 * @returns {string} A formatted string with the correct unit size.
 */
export function convertCommonFileSizeToFriendlyFormat(
  fileSizeInBytes: number,
  includeBytes: boolean
) {
  let sOutput = fileSizeInBytes + " bytes";

  const commonFileSizes = Object.values(COMMON_FILE_SIZES);

  for (
    let nMultiple = 0, nApprox = fileSizeInBytes / 1000;
    nApprox > 1;
    nApprox /= 1000, nMultiple++
  ) {
    sOutput = nApprox.toFixed(3) + " " + commonFileSizes[nMultiple];

    if (includeBytes) sOutput += " (" + fileSizeInBytes + " bytes)";
  }

  return sOutput;
}

// These are exhaustive - shouldn't need anything over MB for a while at least. ;)
const BIT = "b";
const BYTE = "B";
const KILO_BYTE = "KB";
const KIBI_BYTE = "KiB";
const MEGA_BYTE = "MB";
const MEBI_BYTE = "MiB";
const GIGA_BYTE = "GiB";
const TERA_BYTE = "TiB";
const PETA_BYTE = "PiB";
const EXA_BYTE = "EiB";
const ZETTA_BYTE = "ZiB";
const YOTTA_BYTE = "YiB";

/**
 * @const
 * @type {(string)[]}
 * @description An array of all file size measurements.
 * You may want to use the curated {@link ALLOWED_FILE_SIZE_NAMES} array instead.
 */
export const FILE_SIZE_NAMES = [
  BIT,
  BYTE,
  KILO_BYTE,
  KIBI_BYTE,
  MEGA_BYTE,
  MEBI_BYTE,
  GIGA_BYTE,
  TERA_BYTE,
  PETA_BYTE,
  EXA_BYTE,
  ZETTA_BYTE,
  YOTTA_BYTE,
];
/**
 * @const
 * @type { object }
 * @description An object to reference for all file size measurements allowed in this app so far.
 * If you need more look at {@link FILE_SIZE_NAMES }.
 * @example
 * const fileSizeUnit = ALLOWED_FILE_SIZE_NAMES.MEGA_BYTE;
 */
export const ALLOWED_FILE_SIZE_NAMES = {
  BIT: BIT,
  BYTE: BYTE,
  KILO_BYTE: KILO_BYTE,
  KIBI_BYTE: KIBI_BYTE,
  MEGA_BYTE: MEGA_BYTE,
  MEBI_BYTE: MEBI_BYTE,
};

export const COMMON_FILE_SIZES = {
  KILO_BYTE: KILO_BYTE,
  MEGA_BYTE: MEGA_BYTE,
  GIGA_BYTE: GIGA_BYTE,
};

/**
 * @function
 * @param size {string} One of {@link ALLOWED_FILE_SIZE_NAMES}
 * @returns {number} size in bytes.
 * @example
 * let limit = 5 * getFileMultiplicationFactorByUnitName(ALLOWED_FILE_SIZE_NAMES.KILO_BYTE); // returns 5000
 */
export function getFileMultiplicationFactorByUnitName(size: string) {
  let sizeInBytes;

  switch (size) {
    case BIT:
      sizeInBytes = 1 / 8;
      break;
    case BYTE:
      sizeInBytes = 1;
      break;
    case KILO_BYTE:
      sizeInBytes = 1000;
      break;
    case KIBI_BYTE:
      sizeInBytes = 1024;
      break;
    case MEGA_BYTE:
      sizeInBytes = 1000 * 1000;
      break;
    case MEBI_BYTE:
      sizeInBytes = 1024 * 1024;
      break;
    case GIGA_BYTE:
      sizeInBytes = 1024 * 1024 * 1024;
      break;
    default:
      sizeInBytes = 1;
  }

  return sizeInBytes;
}
